import { Edit2, Eye, KeyRound, Plus, Trash2, UserIcon } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AppButton from "../../../components/ui/AppButton";
import DashboardFilterBar, {
  boolFromToggleFilter,
  isAllFilter,
  STATUS_FILTER_OPTIONS,
} from "../../../components/ui/dashboard/DashboardFilterBar";
import Pagination from "../../../components/ui/dashboard/Pagination";
import Table, { Column } from "../../../components/ui/dashboard/Table";
import ThreeDotsMenu from "../../../components/ui/ThreeDotsMenu";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import { useResendCredentials } from "../../../hooks/useResendCredentials";
import { buildApiUrl, cn } from "../../../lib/utils";
import { User, UserSearchRequest, UserSearchResponse } from "../@types";
import { useGetUsersMutation } from "../userApiSlice";
import { default as UserModal } from "../UserModal";
import DeleteUserModal from "../ui/DeleteUserModal";

const UserDashboardTab: React.FC = () => {
  const navigate = useNavigate();
  const { resend: resendCredentials } = useResendCredentials();
  const location = useLocation();

  const [currentTab] = location.pathname.split("/").filter(Boolean);

  // State management
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);

  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const [isUpdateUserModalOpen, setIsUpdateUserModalOpen] = useState(false);
  const [isDeleteUserModalOpen, setIsDeleteUserModalOpen] = useState(false);
  const debouncedKeyword = useDebouncedValue(searchTerm.trim());

  const [getUsers, { data: usersData, isLoading }] = useGetUsersMutation();

  const fetchUsers = useCallback(async () => {
    const requestBody: UserSearchRequest = {
      pageNumber: currentPage,
      pageSize,
      keyword: debouncedKeyword || undefined,
      isActive: boolFromToggleFilter(statusFilter, "active", "inactive"),
    };

    try {
      await getUsers(requestBody).unwrap();
    } catch (err) {
      console.error("Failed to fetch users:", err);
    }
  }, [currentPage, pageSize, debouncedKeyword, statusFilter, getUsers]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedKeyword, statusFilter]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  const handleAddUser = (open: boolean) => {
    setIsAddUserModalOpen(open);
  };

  // Table columns configuration
  const columns: Column<User>[] = [
    {
      key: "userName",
      header: "Username",
      width: "300px",
      render: (value: string, item: User) => (
        <div className="flex items-center space-x-3">
          {item.imageUrl ? (
            <img
              src={buildApiUrl(item.imageUrl)}
              alt={`${item.userName}`}
              className="w-[30px] h-[30px] shrink-0 rounded-full object-cover border border-gray-300 shadow-sm"
            />
          ) : (
            <div className="w-[30px] h-[30px] shrink-0 rounded-full bg-gray-200 flex items-center justify-center border border-gray-300 shadow-sm p-1">
              <UserIcon className="w-full h-full text-hColor" />
            </div>
          )}
          <div className="flex min-w-0 flex-col">
            <span className="text-hColor capitalize">{item.fullName}</span>
            <span className="text-hColor text-sm font-normal text-pColor break-words">
              {item.email}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: "userType",
      header: "User type",
      width: "250px",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "phoneNumber",
      header: "Phone number",
      width: "250px",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "address",
      header: "Home address",
      width: "250px",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "isActive",
      header: "Status",
      width: "250px",
      render: (value: string, item: User) => {
        return (
          <span
            className={cn(
              "text-hColor px-2 py-1 text-[12px] font-semibold",
              item.isActive
                ? "text-green-500 border border-green-500 bg-green-50 rounded-full"
                : "text-hColor border border-hColor rounded-full"
            )}
          >
            {item.isActive ? "Active" : "Inactive"}
          </span>
        );
      },
    },
    {
      key: "actions",
      header: "",
      width: "12",
      align: "center" as const,
      render: (value: any, item: User) => {
        return (
          <ThreeDotsMenu
            actions={[
              {
                label: "Edit",
                onClick: () => {
                  setSelectedUser(item);
                  setIsUpdateUserModalOpen(true);
                },
                icon: <Edit2 className="w-4 h-4" />,
              },
              {
                label: "View user",
                onClick: () => navigate(`/${currentTab}/user/${item.id}`),
                icon: <Eye className="w-4 h-4" />,
              },
              {
                label: "Resend credentials",
                onClick: () => resendCredentials(item.email),
                icon: <KeyRound className="w-4 h-4" />,
              },
              {
                label: "Delete user",
                onClick: () => {
                  setSelectedUser(item);
                  setIsDeleteUserModalOpen(true);
                },
                icon: <Trash2 className="w-4 h-4" />,
              },
            ]}
          />
        );
      },
    },
  ];

  const handleClose = () => {
    fetchUsers();
    handleAddUser(false);
    setIsUpdateUserModalOpen(false);
    setIsDeleteUserModalOpen(false);
  };

  const activeFilters = useMemo(() => {
    const chips: { key: string; label: string; onRemove: () => void }[] = [];
    if (debouncedKeyword) {
      chips.push({
        key: "keyword",
        label: `Search: ${debouncedKeyword}`,
        onRemove: () => setSearchTerm(""),
      });
    }
    if (!isAllFilter(statusFilter)) {
      chips.push({
        key: "status",
        label: `Status: ${STATUS_FILTER_OPTIONS.find((o) => o.value === statusFilter)?.label}`,
        onRemove: () => setStatusFilter("all"),
      });
    }
    return chips;
  }, [debouncedKeyword, statusFilter]);

  const response = usersData?.data as UserSearchResponse | undefined;

  return (
    <div>
      <div className="space-y-6">
        {/* Filters and Actions */}
        <div className="bg-white rounded-[10px] p-4 space-y-4">
          <DashboardFilterBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onClearSearch={() => setSearchTerm("")}
            searchPlaceholder="Search by name, email or role"
            isLoading={isLoading}
            filters={[
              {
                id: "status",
                placeholder: "Status",
                value: statusFilter,
                options: STATUS_FILTER_OPTIONS,
                onChange: (value) => setStatusFilter(value || "all"),
              },
            ]}
            chips={activeFilters}
            onClearAll={() => {
              setSearchTerm("");
              setStatusFilter("all");
            }}
            resultCount={response?.totalCount}
            resultLabel={response?.totalCount === 1 ? "user" : "users"}
            actions={
              <AppButton
                onClick={() => handleAddUser(true)}
                className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors w-full sm:w-auto sm:min-w-[146px] justify-center h-[42px]"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add user
              </AppButton>
            }
          />
        </div>

        {/* Table */}
        <div className="overflow-hidden space-y-6">
          <Table
            data={response?.data || []}
            columns={columns}
            loading={isLoading}
            emptyMessage="No users found"
          />

          {/* Pagination */}
          {response && (
            <Pagination
              currentPage={response.currentPage}
              totalPages={response.totalPages}
              pageSize={response.pageSize}
              totalCount={response.totalCount}
              hasNextPage={response.hasNextPage}
              hasPreviousPage={response.hasPreviousPage}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </div>
      </div>
      {isAddUserModalOpen && (
        <UserModal isOpen={isAddUserModalOpen} onClose={handleClose} />
      )}

      {isUpdateUserModalOpen && selectedUser && (
        <UserModal
          isOpen={isUpdateUserModalOpen}
          onClose={handleClose}
          user={selectedUser}
          mode="edit"
        />
      )}

      <DeleteUserModal
        isOpen={isDeleteUserModalOpen}
        onClose={handleClose}
        userId={selectedUser?.id}
        entityLabel="user"
      />
    </div>
  );
};

export default UserDashboardTab;
